// src/lib/privacy/dataExport.ts

import { ExportedUserData } from '../../types/privacyRequest';

/**
 * Converts exported user data to JSON string
 */
export function exportToJSON(data: ExportedUserData): string {
  return JSON.stringify(data, null, 2);
}

/**
 * Converts exported user data to CSV format
 * Creates separate CSV files for each collection type
 */
export function exportToCSV(data: ExportedUserData): string {
  const csvLines: string[] = [];
  
  // CSV Header with metadata
  csvLines.push('# Help Keep My Money - Data Export');
  csvLines.push(`# Exported At: ${data.exportMetadata.exportedAt}`);
  csvLines.push(`# User ID: ${data.exportMetadata.userId}`);
  csvLines.push(`# Email: ${data.exportMetadata.email}`);
  csvLines.push('');
  
  // Profile Section
  if (data.profile) {
    csvLines.push('## Profile');
    csvLines.push('Field,Value');
    Object.entries(data.profile).forEach(([key, value]) => {
      if (key !== 'id') {
        const csvValue = typeof value === 'string' ? value.replace(/"/g, '""') : String(value ?? '');
        csvLines.push(`"${key}","${csvValue}"`);
      }
    });
    csvLines.push('');
  }
  
  // Business Data Section
  if (data.businessData) {
    csvLines.push('## Business Data');
    csvLines.push('Field,Value');
    Object.entries(data.businessData).forEach(([key, value]) => {
      if (key !== 'id') {
        const csvValue = typeof value === 'string' ? value.replace(/"/g, '""') : String(value ?? '');
        csvLines.push(`"${key}","${csvValue}"`);
      }
    });
    csvLines.push('');
  }
  
  // Tasks Section
  if (data.tasks && data.tasks.length > 0) {
    csvLines.push('## Tasks');
    const taskHeaders = ['ID', 'Title', 'Description', 'Status', 'Priority', 'Due Date', 'Created At'];
    csvLines.push(taskHeaders.join(','));
    data.tasks.forEach((task: any) => {
      const row = [
        task.id || '',
        task.title || '',
        (task.description || '').replace(/"/g, '""'),
        task.status || '',
        task.priority || '',
        task.dueDate || '',
        task.createdAt || ''
      ];
      csvLines.push(row.map(v => `"${v}"`).join(','));
    });
    csvLines.push('');
  }
  
  // Documents Section
  if (data.documents && data.documents.length > 0) {
    csvLines.push('## Documents');
    const docHeaders = ['ID', 'Name', 'Type', 'Size', 'Uploaded At'];
    csvLines.push(docHeaders.join(','));
    data.documents.forEach((doc: any) => {
      const row = [
        doc.id || '',
        doc.name || doc.fileName || '',
        doc.type || '',
        doc.size || doc.fileSize || '',
        doc.uploadedAt || doc.createdAt || ''
      ];
      csvLines.push(row.map(v => `"${v}"`).join(','));
    });
    csvLines.push('');
  }
  
  // Subscriptions Section
  if (data.subscriptions && data.subscriptions.length > 0) {
    csvLines.push('## Subscriptions');
    const subHeaders = ['ID', 'Plan', 'Status', 'Amount', 'Created At'];
    csvLines.push(subHeaders.join(','));
    data.subscriptions.forEach((sub: any) => {
      const row = [
        sub.id || '',
        sub.plan || '',
        sub.status || '',
        sub.amount || '',
        sub.createdAt || ''
      ];
      csvLines.push(row.map(v => `"${v}"`).join(','));
    });
    csvLines.push('');
  }
  
  return csvLines.join('\n');
}

/**
 * Downloads data as a file
 */
export function downloadData(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exports user data and triggers download
 */
export function exportUserData(data: ExportedUserData, format: 'json' | 'csv') {
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `hkmm-data-export-${timestamp}.${format}`;
  
  if (format === 'json') {
    const jsonContent = exportToJSON(data);
    downloadData(jsonContent, filename, 'application/json');
  } else {
    const csvContent = exportToCSV(data);
    downloadData(csvContent, filename, 'text/csv');
  }
}
